import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { Environment } from '@r1-compass-library/environments/environment';
import { IamRole } from '@r1-compass-library/models';
import { catchError, finalize, Observable, of, retry } from 'rxjs';
import { SpinnerService } from './spinner.service';

@Injectable({ providedIn: 'root' })
export class IamRoleService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private BASE_URL: string = Environment.baseUrl + '/frontend/iam-roles';

    get(id: number): Observable<IamRole> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamRoleService.get';
        this.#spinnerService.addProcess(process, 'Getting IAM role...');
        return this.#http.get<IamRole>(`${this.BASE_URL}/${id}`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<IamRole>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    create(iamRole: Partial<IamRole>): Observable<IamRole> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamRoleService.create';
        this.#spinnerService.addProcess(process, 'Creating IAM role...');
        return this.#http.post<IamRole>(`${this.BASE_URL}`, iamRole, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamRole>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    update(id: number, iamRole: Partial<IamRole>): Observable<IamRole> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
            }),
        };
        const process = 'IamRoleService.update';
        this.#spinnerService.addProcess(process, 'Updating IAM role...');
        return this.#http.patch<IamRole>(`${this.BASE_URL}/${id}`, iamRole, httpOptions).pipe(
            catchError(error => {
                return of<IamRole>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    findAll(): Observable<IamRole[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamRoleService.findAll';
        this.#spinnerService.addProcess(process, 'Getting IAM roles...');
        return this.#http.get<IamRole[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamRole[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }
}
